import React, {useState, useEffect, useContext} from 'react'

//Node Modules
import { useTranslation } from 'react-i18next'
import { Button, Card, Container } from 'react-bootstrap'

//Components
import Loading from 'components/Loading'
import LinkButton from 'components/LinkButton'
import SecureModal from 'components/SecureModal'
import { LockedIcon} from 'components/SASIcon'
import DownloadSettings from './DownloadSettings'
import DownloadFileSelector from './DownloadFileSelector'

//Styles
import styles from './DownloadContainer.module.scss'

//Context 
import { ToggleSecureContext } from 'context/content-context'

//Api
import * as downloadApi from 'api/download'
import * as api from 'api/secure'
import {downloadInfo} from 'api/cdc'

//Types
import { BaseCDCInfo } from 'types/cdc'
import { DownloadTOC, DownloadNode, DownloadChildNode as ChildNodeType, DownloadParentNode as ParentNodeType} from 'types/download'

//Util
import {Language} from 'util/languages'

interface Props {
  softwareId?: string,
  softwareVersion?: string,
  softwareSpecialConfig?: string,
  inapp: boolean
}

const DownloadContainer =(
  {
    softwareId,
    softwareVersion,
    softwareSpecialConfig,
    inapp
  }: Props) => {

  const [pageInfo, setPageInfo] = useState<BaseCDCInfo>()
  const [downloadText, setDownloadText] = useState('')
  const [uniqueId, setUniqueId] = useState('')
  const [downloadToc, setDownloadToc] = useState<DownloadTOC>()
  const [selectedLang, setSelectedLang] = useState<Language>({ code:"en", english: "English", name:"English" })
  const [selectedCas, setSelectedCas] = useState({key: 'SAS', value: 'SAS'})
  const [selectedFormat, setSelectedFormat] = useState({key:'ZIP', value:'.zip'})
  const [isSecure, setIsSecure] = useState(false)
  const [isLocked, setIsLocked]= useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadClicked, setDownloadClicked] = useState(false)
  const [showSecureModal, setShowSecureModal] = useState(false)
  const toggleSecureContext = useContext(ToggleSecureContext)
  const { t } = useTranslation()

  useEffect(()=> {

    async function load() {
      if (softwareId && softwareVersion) {
        let id
        let version
        await downloadInfo(selectedLang.code, softwareId, softwareVersion, softwareSpecialConfig).then(info => {
          id=info.id
          version = info.version
          setPageInfo(info)
        }, ()=>{setIsLoading(false)})

        if (inapp) {
          await api.cdcSecureKey(id || '', version || '', '', true)
        }

        await  downloadApi.downloadToc(selectedLang.code, softwareId, softwareVersion, softwareSpecialConfig).then( downloadTocInfo=> {
          setDownloadToc(downloadTocInfo.pdfToc)
          setDownloadText(downloadTocInfo.pdfDownloadText || '')
          setUniqueId(downloadTocInfo.uniqueId)
        }, ()=> {setIsLoading(false)})
      }
      setIsLoading(false)
    }
    load()
    
  },[softwareId, softwareVersion, softwareSpecialConfig, selectedLang, toggleSecureContext.secureToggled, inapp])

  useEffect(()=> {
    const findSecureLock = (toc: DownloadNode[]):void => {
      toc.forEach(node => {
        if (node.type === 'include') {
          let check = node as ChildNodeType
          if (check.isSecure) {
            setIsSecure(true)
            setIsLocked(check.isLocked)
          }
        } else if (node.type === 'parent') {
          let parent = node as ParentNodeType
          findSecureLock(parent.children)
        }
      })
    }
    if (downloadToc?.toc && downloadToc.toc.length > 0) {
      findSecureLock(downloadToc.toc)
    }
  }, [downloadToc])

  useEffect( ()=> {
    if (isSecure && isLocked) {
      setShowSecureModal(true)
    }
  }, [isSecure, isLocked])

  useEffect(()=> {
    setDownloadClicked(false)
  },[selectedLang, selectedCas, selectedFormat])

  const setToc = (toc?:DownloadTOC):void => {
    if (toc) {
      let newToc = JSON.parse(JSON.stringify(toc)) 
      setDownloadToc(newToc)
    }
  }

  const showModal = () => {
    setShowSecureModal(true)
  } 

  const submitKey = (accessKey: string): Promise<void> => {
    return api.downloadSecureKey(pageInfo?.id || '', pageInfo?.version || '', accessKey).then(()=>setShowSecureModal(false))
  }

  const download = () => {
    setDownloadClicked(true)
    if (softwareId && softwareVersion) {
      setIsDownloading(true)
      let dto = {
        uniqueId: uniqueId,
        casLangSelection: selectedCas.key,
        downloadType: selectedFormat.key,
        pdfDownloadText: downloadText,
        pdfToc: downloadToc
      }
      downloadApi.downloadFiles(dto, selectedLang.code, softwareId,softwareVersion, softwareSpecialConfig).then( () => {
        downloadFile(dto,softwareId, softwareVersion,softwareSpecialConfig, 0)
      }, () => {
        setIsDownloading(false)
        setTimeout(function(){
          downloadApi.downloadComplete(dto, selectedLang.code, softwareId,softwareVersion, softwareSpecialConfig);
        },30000)
      })
    }
  }

  const downloadFile = (dto: any, softwareId: string, softwareVersion:string , softwareSpecialConfig: string | undefined, attempts: number) => {
    //create xml http request
    let url = "api/packages/download/" + uniqueId;
    let xhr = new XMLHttpRequest()
    xhr.open('GET', url)
    xhr.responseType = "blob";

    //send the request
    xhr.send()

    //call response
    xhr.onload = () => {
    if (xhr.status === 500) { //if call is unsuccessful, reattempt at most 20 times
      if (attempts < 21) {
        downloadFile(dto, softwareId, softwareVersion, softwareSpecialConfig, attempts+1)
      } else {
        setIsDownloading(false)
      }
    } else {  //call returned successfully
      let blob = xhr.response;
      let contentDispo = xhr.getResponseHeader('Content-Disposition') || 'filename=download'+selectedFormat.value;
      let fileName = contentDispo.substring(contentDispo?.indexOf('filename=')+9, contentDispo.length)

      if(window.navigator.msSaveOrOpenBlob) {
        // IE11
        window.navigator.msSaveOrOpenBlob(blob, fileName);
    } else {
      let  blobUrl  = window.URL.createObjectURL(blob);
      let a = document.createElement("a");
      document.body.appendChild(a);
      a.href = blobUrl;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
    }

      setIsDownloading(false)
      setTimeout(function(){
        downloadApi.downloadComplete(dto, selectedLang.code, softwareId,softwareVersion, softwareSpecialConfig);
      },30000)
    }
    }
  }
  


  return (
    <article id="DownloadPageContainer" className={styles.downloadPageContainer}>
      {pageInfo ? <SecureModal show={showSecureModal} onHide={()=>{setShowSecureModal(false)}} submit={submitKey}/> : null}
      <Container fluid>
        <Card>
          <Card.Header className={styles.cardHeader}>{}
            {t('helpcenter-gui-icu.downloadpage.header.title')}{pageInfo && pageInfo.title ? " / "+pageInfo.title.value : ''}
          </Card.Header>
          {
            isLoading ? <Loading /> :
              <Card.Body>
                <section className={styles.downloadTextContainer}>
                  <label>{t('helpcenter-gui-icu.downloadpage.body.download_message.label')}</label>
                  <label>{downloadText}</label>
                </section>
                <section className={styles.downloadSettingsContainer}>
                  <DownloadSettings 
                    selectedLang={selectedLang}
                    setSelectedLang = {setSelectedLang}
                    selectedCas={selectedCas}
                    setSelectedCas={setSelectedCas}
                    selectedFormat={selectedFormat}
                    setSelectedFormat={setSelectedFormat}/>
                </section>
                <section className={styles.downloadFileSelectorContainer}>
                  <label>
                    {t("helpcenter-gui-icu.downloadpage.fileselector.available.label")} - {isSecure && isLocked ? <LinkButton className={styles.linkButton} onClick={showModal}><b>{t("helpcenter-gui-icu.downloadpage.fileselector.submit_access_key.label")}</b></LinkButton> : null}
                  </label>
                  <DownloadFileSelector 
                    title={pageInfo && pageInfo.title && pageInfo.title.value}
                    tocInfo={downloadToc}
                    setToc={setToc}/>
                    <div className={styles.recommendedLegend}><label><b className={styles.recommended}>*</b> {t("helpcenter-gui-icu.downloadpage.legend.recommended.label")}</label></div>
                    {
                      isSecure && isLocked  ? <div><label><LockedIcon/>{t('helpcenter-gui-icu.downloadpage.legend.secure1.label')}  <LinkButton className={styles.linkButton} onClick={showModal}><b>{t("helpcenter-gui-icu.downloadpage.legend.secure_submit.label")}</b></LinkButton> {t('helpcenter-gui-icu.downloadpage.legend.secure2.label')}</label></div> : null 
                    }
                    <div className={styles.downloadBtnContainer}> 
                      {isDownloading ? <Loading/> : 
                        downloadClicked ? <div>{t("helpcenter-gui-icu.downloadpage.download_section.download_message.msg") }<LinkButton className={styles.linkButton} onClick={download}>{t("helpcenter-gui-icu.downloadpage.download_section.click_here.msg")}</LinkButton></div> : 
                          <Button variant="primary" size="sm" onClick={download}>{t("helpcenter-gui-icu.downloadpage.download_section.download_btn.label")}</Button>}
                    </div>
                </section>
              </Card.Body>
          }
        </Card>
      </Container>
    </article>
  )
}

export default DownloadContainer


